import React from "react";
import { Subscription } from "rxjs";
import EnvironmentService from "../../utilities/environmentService";
import { InkAndTonerService } from "../../utilities/inkAndTonerService";
import {
  IInkAndTonerBrand,
  IInkAndTonerDevice,
  IInkAndTonerModel,
  IInkAndTonerOption,
  IInkAndTonerPayload,
  ITopBrand,
} from "./inkAndTonerDropdown";
import "./inkAndTonerFinder.scss";
import "./selectYourXDropdown.scss";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

const IS_SERVER_SIDE = false;
// eslint-disable-next-line @typescript-eslint/no-var-requires, no-undef
const SelectYourX = require(IS_SERVER_SIDE
  ? "./inkAndTonerDropdownDummy.tsx"
  : "./selectYourX.tsx").default;

export interface IInkAndTonerFinderProps {
  isMobile?: boolean;
}

export interface IInkAndTonerFinderState {
  brands: IInkAndTonerOption[];
  models: IInkAndTonerOption[];
  devices: IInkAndTonerOption[];
  searchFinderType: string;
  brand: string;
  device: string;
  model: string;
  topBrands: ITopBrand[];
  openDropdownIndex: number;
  shopUrl: string;
}

class InkAndTonerFinder extends React.Component<
  IInkAndTonerFinderProps,
  IInkAndTonerFinderState
> {
  private getDevicesSub: Subscription | undefined;
  private getModelsSub: Subscription | undefined;
  private inkAndTonerSub: Subscription | undefined;

  constructor(props: IInkAndTonerFinderProps) {
    super(props);

    this.state = {
      brands: [],
      models: [],
      devices: [],
      searchFinderType: "",
      brand: "",
      device: "",
      model: "",
      topBrands: [],
      openDropdownIndex: -1,
      shopUrl: WBMUrlService.getShopUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomSearch!
      ),
    };

    this.setBrandValue = this.setBrandValue.bind(this);
    this.setDeviceValue = this.setDeviceValue.bind(this);
    this.setModelValue = this.setModelValue.bind(this);
    this.setOpenDropdownIndex = this.setOpenDropdownIndex.bind(this);
  }

  setBrandValue(value: string) {
    this.setState(
      {
        brand: value,
        device: "",
        model: "",
        openDropdownIndex: 1,
      },
      () => {
        if (this.state.brand) {
          if (this.getDevicesSub) {
            this.getDevicesSub.unsubscribe();
          }
          this.getDevicesSub = InkAndTonerService.getDevices(
            this.state.brand,
            this.state.searchFinderType
          ).subscribe((devices) => {
            this.setState({
              devices: devices.map((device: IInkAndTonerDevice) => {
                return {
                  label: device.description,
                  value: device.description,
                };
              }),
            });
          });
        }
      }
    );
  }

  setDeviceValue(value: string) {
    this.setState(
      {
        device: value,
        model: "",
        openDropdownIndex: 2,
      },
      () => {
        if (this.state.brand && this.state.device) {
          if (this.getModelsSub) {
            this.getModelsSub.unsubscribe();
          }
          this.getModelsSub = InkAndTonerService.getModels(
            this.state.brand,
            this.state.device,
            this.state.searchFinderType
          ).subscribe((models) => {
            this.setState({
              models: models.map((model: IInkAndTonerModel) => {
                return {
                  label: model.description,
                  value: model.description,
                };
              }),
            });
          });
        }
      }
    );
  }

  setModelValue(value: string) {
    this.setState(
      {
        model: value,
      },
      () => {
        if (this.state.brand && this.state.device && this.state.model) {
          const queryString =
            "?Finder=Type||" +
            this.state.searchFinderType +
            "&Finder=Manufacturer||" +
            encodeURI(this.state.brand) +
            "&Finder=Device||" +
            encodeURI(this.state.device) +
            "&Finder=Model||" +
            encodeURI(this.state.model) +
            "&sc=BM&fi=1&fr=1";

          window.location.href = `${this.state.shopUrl}${queryString}`;
        }
      }
    );
  }

  setOpenDropdownIndex(index: number) {
    this.setState({
      openDropdownIndex: index,
    });
  }

  componentWillUnmount() {
    if (this.getDevicesSub) {
      this.getDevicesSub.unsubscribe();
    }

    if (this.getModelsSub) {
      this.getModelsSub.unsubscribe();
    }

    if (this.inkAndTonerSub) {
      this.inkAndTonerSub.unsubscribe();
    }
  }

  componentDidMount() {
    this.inkAndTonerSub = InkAndTonerService.getPayload().subscribe(
      (payload: IInkAndTonerPayload) => {
        this.setState({
          brands: payload.brands.map((brand: IInkAndTonerBrand) => {
            return {
              label: brand.description,
              value: brand.description,
            };
          }),
          searchFinderType:
            payload.configPropValues &&
            payload.configPropValues["Ink and Toner Finder Type"]
              ? payload.configPropValues["Ink and Toner Finder Type"]
              : "InkAndTonerMatch",
          topBrands: payload.brands
            .filter((brand) => brand.filterValueDisplayStyle === "Top")
            .map((brand) => {
              return {
                alt: brand.description,
                src: brand.filterValueGraphic,
              };
            }),
        });
      }
    );
  }

  render() {
    return (
      <div
        className={
          "w-100 scroll-applied ink-and-toner-finder" +
          (this.props.isMobile ? " is-mobile" : "")
        }
      >
        <div className="container mt-3">
          <div className="mb-2">
            <h5>Select Your Printer</h5>
          </div>

          <div className="row dropdown-margin-bottom">
            <div
              className={this.props.isMobile ? "col-12 form-group" : "col-4"}
            >
              <SelectYourX
                value={this.state.brand}
                arrayForSelection={this.state.brands}
                selectYourXDropdownIndex={0}
                buttonText={"Select Printer Brand"}
                filterPlaceholderText={" Search for a Brand"}
                callBack={this.setBrandValue}
                setOpenDropdownIndex={this.setOpenDropdownIndex}
                openDropdownIndex={this.state.openDropdownIndex}
              />
            </div>
            <div
              className={this.props.isMobile ? "col-12 form-group" : "col-4"}
            >
              <SelectYourX
                value={this.state.device}
                arrayForSelection={this.state.devices}
                selectYourXDropdownIndex={1}
                buttonText={"Select Printer Series"}
                filterPlaceholderText={" Search for a Series"}
                callBack={this.setDeviceValue}
                isDisabled={!this.state.brand}
                setOpenDropdownIndex={this.setOpenDropdownIndex}
                openDropdownIndex={this.state.openDropdownIndex}
              />
            </div>
            <div className={this.props.isMobile ? "col-12" : "col-4"}>
              <SelectYourX
                value={this.state.model}
                arrayForSelection={this.state.models}
                selectYourXDropdownIndex={2}
                buttonText={"Select Printer Model"}
                filterPlaceholderText={" Search for a Model"}
                callBack={this.setModelValue}
                isDisabled={!this.state.device}
                setOpenDropdownIndex={this.setOpenDropdownIndex}
                openDropdownIndex={this.state.openDropdownIndex}
              />
            </div>
          </div>

          {this.state.topBrands.length > 0 && (
            <>
              <div>
                <h5>Choose From Featured Brands</h5>
              </div>
              <div>
                <hr />
              </div>
              <div
                className={
                  "ink-and-toner-top-brands" +
                  (this.props.isMobile ? " container" : "")
                }
              >
                {this.state.topBrands &&
                  this.state.topBrands.map((brand) => {
                    return (
                      <span
                        key={brand.alt}
                        className={
                          this.props.isMobile ? "top-brands-mobile col-6" : ""
                        }
                      >
                        <img
                          src={brand.src}
                          alt={brand.alt}
                          onClick={() => {
                            this.setBrandValue(brand.alt);
                          }}
                          role="button"
                        />
                      </span>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default InkAndTonerFinder;
